import { BaseViewStore } from 'core/stores';
import { AssignCarrierAccountForm } from 'modules/order/forms';
import { action, makeObservable } from 'mobx';
import FulfillmentOrderProcessingStore from './FulfillmentOrderProcessingStore';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import { LookupStore } from 'modules/lookup/stores';
import { carrierTypes, fulfillmentOrderAction, responseStatus, sourceOfChange } from 'common/constants';
import { isSome, notificationError, translate } from 'core/utils';
import { BaasicDropdownViewStore } from 'common/stores';
import _ from 'lodash';
import { assignCarrierAccountStatuses } from 'common/constants/fulfillmentOrderConstants';
import { CarrierStore } from 'modules/carrier/stores';
import { ResultReport } from '../pages';

class AssignCarrierAccountViewStore extends BaseViewStore {
	selectedItem = null;
	selectedItems = null;

	form = new AssignCarrierAccountForm({
		onSuccess: async () => {
			if (this.selectedItems == null) {
				await this.assign();
			} else {
				await this.assignBulk();
			}
			this.modalParams.success();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.lookupStore = new LookupStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.fulfillmentOrderProcessingStore = new FulfillmentOrderProcessingStore(rootStore);
		this.initialize(_.get(this.modalParams, 'data.selectedItemId'), _.get(this.modalParams, 'data.selectedItemIds'));
	}

	@action.bound
	initialize = async (selectedItemId, selectedItemIds) => {
		this.suspend();
		if (selectedItemId != null) {
			await this.getFulfillmentOrder(selectedItemId);
			if (!this.selectedItem.carrier) {
				this.form.$('carrierId').set('rules', 'required');
			}
		} else {
			await this.findFulfillmentOrders(selectedItemIds);
			if (this.selectedItems.every(item => !item.carrier)) {
				this.form.$('carrierId').set('rules', 'required');
			}
		}
		this.carrierDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('carrierId'),
			store: new CarrierStore(this.rootStore),
			value: this.setInitialCarrier(),
			filterProperties: {
				sellerIds: selectedItemId != null ? [this.selectedItem.sellerId] : this.selectedItems.map(item => item.sellerId),
				carrierTypeNames: [carrierTypes.ltl],
			},
		});
		this.resume();
	};

	@action.bound
	setInitialCarrier = () => {
		if (this.selectedItem && this.selectedItem.carrier != null) {
			return {
				value: this.selectedItem.assignedCarrierId,
				label: this.selectedItem.carrier.name,
			};
		}

		if (this.selectedItems) {
			let selectedItemsWithCarrier = this.selectedItems.filter(item => isSome(item.carrier));

			if (selectedItemsWithCarrier != null && selectedItemsWithCarrier.length > 0) {
				let carrier = selectedItemsWithCarrier[0].carrier;
				let selectedItemsWithSameCarrier = selectedItemsWithCarrier.filter(
					item => item.assignedCarrierId == carrier.id
				);

				if (selectedItemsWithSameCarrier.length == selectedItemsWithCarrier.length) {
					return carrier;
				}
			}
		}
	};

	@action.bound
	findFulfillmentOrders = async selectedItemIds => {
		this.selectedItems = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			ids: selectedItemIds,
			embed: ['fulfillmentOrderStatus', 'carrier', 'order'],
		})).item;
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: ['fulfillmentOrderStatus', 'carrier', 'order'],
		});
	};

	@action.bound
	assign = async () => {
		this.suspend();
		try {
			const { carrierId } = this.form.values();
			const assignParams = { fulfillmentOrderIds: [this.selectedItem.id], carrierId: carrierId };
			const response = (await this.fulfillmentOrderProcessingStore.assignCarrierAccount(
				assignParams,
				sourceOfChange.assignCarrierAccount
			))[0];

			if (response.status === responseStatus.success) {
				this.rootStore.notificationStore.success([response.message]);
			} else if (response.status === responseStatus.warning) {
				this.rootStore.notificationStore.warning([[response.message]]);
			} else {
				this.rootStore.notificationStore.error([[response.message]]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	assignBulk = async () => {
		try {
			const { carrierId } = this.form.values();
			const selectedOrderIds = [];
			const notProcessableOrders = [];
			this.selectedItems.map(selectedItem => {
				let index = _.findIndex(assignCarrierAccountStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index != -1) {
					selectedOrderIds.push(selectedItem.id);
				} else {
					notProcessableOrders.push(selectedItem);
				}
			});
			const assignBulkParams = { fulfillmentOrderIds: selectedOrderIds, carrierId: carrierId };
			this.modalParams.close();
			const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.ASSIGNCARRIERACCOUNT'));
			const response = await this.fulfillmentOrderProcessingStore.assignCarrierAccount(
				assignBulkParams,
				sourceOfChange.assignCarrierAccountBulk
			);
			this.rootStore.notificationStore.dismiss(notificationId);

			let notificationParams = {
				autoClose: response.every(item => item.status === responseStatus.success),
				onClick: () => {
					this.openModal({
						data: response,
						component: ResultReport,
						fulfillmentOrderAction: fulfillmentOrderAction.BulkAssignCarrierAccount,
					});
				},
			};

			if (response.every(item => item.status === responseStatus.error)) {
				this.rootStore.notificationStore.error('ASSIGNCARRIERACCOUNTVIEW.ERRORBULK', false, notificationParams);
			} else if (response.every(item => item.status === responseStatus.success)) {
				this.rootStore.notificationStore.success('ASSIGNCARRIERACCOUNTVIEW.SUCCESSBULK');
			} else {
				this.rootStore.notificationStore.warning('ASSIGNCARRIERACCOUNTVIEW.WARNINGBULK', notificationParams);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};
}

export default AssignCarrierAccountViewStore;
